/**
 * WebSocket Hook for Live Operation
 * Manages WebSocket connection and data updates
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { LiveOperationData } from '@/types/websocket';

interface UseWebSocketOptions {
  onData?: (data: LiveOperationData) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
  onClose?: () => void;
  reconnectAttempts?: number;
  reconnectDelay?: number;
  singleFetch?: boolean; // Connect once and disconnect after receiving first data
}

export function useWebSocket(url: string, options: UseWebSocketOptions = {}) {
  const [isConnected, setIsConnected] = useState(false);
  const [data, setData] = useState<LiveOperationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionInitiatedRef = useRef(false);
  
  const {
    onData,
    onError,
    onOpen,
    onClose,
    reconnectAttempts = 5,
    reconnectDelay = 3000,
    singleFetch = false,
  } = options;

  const dataReceivedRef = useRef(false);

  const connect = useCallback(() => {
    try {
      // Prevent multiple connection attempts
      if (wsRef.current?.readyState === WebSocket.CONNECTING || 
          wsRef.current?.readyState === WebSocket.OPEN) {
        return;
      }

      // In singleFetch mode, prevent reconnection after first attempt
      if (singleFetch && connectionInitiatedRef.current) {
        console.log('[WebSocket] singleFetch mode: connection already initiated, skipping');
        return;
      }

      connectionInitiatedRef.current = true;
      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log('[WebSocket] Connected to', url);
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        onOpen?.();
      };

      ws.onmessage = (event) => {
        try {
          const parsedData: LiveOperationData = JSON.parse(event.data);
          setData(parsedData);
          onData?.(parsedData);
          
          // If singleFetch is enabled, disconnect after receiving first data
          if (singleFetch && !dataReceivedRef.current) {
            dataReceivedRef.current = true;
            console.log('[WebSocket] Data received, disconnecting (singleFetch mode)');
            setTimeout(() => {
              ws.close();
            }, 100);
          }
        } catch (err) {
          console.error('[WebSocket] Failed to parse message:', err);
          setError('Failed to parse WebSocket message');
        }
      };

      ws.onerror = (event) => {
        console.error('[WebSocket] Error:', event);
        setError('WebSocket connection error');
        onError?.(event);
      };

      ws.onclose = () => {
        console.log('[WebSocket] Connection closed');
        setIsConnected(false);
        onClose?.();

        // Skip reconnection if singleFetch mode is enabled
        if (singleFetch) {
          console.log('[WebSocket] singleFetch mode: no reconnection');
          return;
        }

        // Attempt to reconnect
        if (reconnectAttemptsRef.current < reconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          console.log(
            `[WebSocket] Reconnecting... (attempt ${reconnectAttemptsRef.current}/${reconnectAttempts})`
          );
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, reconnectDelay);
        } else {
          setError('WebSocket connection failed after multiple attempts');
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('[WebSocket] Connection error:', err);
      setError(err instanceof Error ? err.message : 'Unknown connection error');
    }
  }, [url, singleFetch]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  const send = useCallback((message: string | object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const payload = typeof message === 'string' ? message : JSON.stringify(message);
      wsRef.current.send(payload);
    } else {
      console.warn('[WebSocket] Cannot send message: WebSocket is not connected');
    }
  }, []);

  useEffect(() => {
    connect();

    return () => {
      // In singleFetch mode, don't disconnect on unmount if data was received
      if (singleFetch && dataReceivedRef.current) {
        return;
      }
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    data,
    error,
    send,
    disconnect,
  };
}
